import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useErrorSnackbar } from "@/hooks/useErrorSnackbar";
import type { ExportFormat } from "./exportGridData";
import { GridExportButton } from "./GridExportButton";

const FAILURE_MESSAGE_KEY: Record<ExportFormat, string> = {
	xlsx: "download.excel.failed",
	csv: "export.file.generation.failed",
};

interface GridExportControlProps {
	/** Builds and downloads the file; rejects when it could not be made. */
	exportRows: (format: ExportFormat) => Promise<void>;
	rowCount: number;
	formats: readonly ExportFormat[];
	disabled: boolean;
	dataTestId?: string;
}

/**
 * The grid toolbar's export, with its progress and failure reporting. A component of its own rather than
 * state inside `SystemDataGrid`, because the error snackbar needs the app's department and snackbar
 * providers — which only a grid that actually offers export should have to sit under.
 */
export const GridExportControl = memo(function GridExportControl({ exportRows, rowCount, formats, disabled, dataTestId }: GridExportControlProps) {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const showErrorSnackbar = useErrorSnackbar();
	const [isExporting, setIsExporting] = useState(false);

	const runExport = useCallback(
		async (format: ExportFormat) => {
			setIsExporting(true);
			track("system_data_grid_export_start", { format, row_count: rowCount });
			try {
				await exportRows(format);
				track("system_data_grid_export_success", { format, row_count: rowCount });
			} catch (error) {
				trackError("system_data_grid_export_error", { format, error_message: error instanceof Error ? error.message : String(error) });
				showErrorSnackbar(t(FAILURE_MESSAGE_KEY[format]), {}, error);
			} finally {
				setIsExporting(false);
			}
		},
		[exportRows, rowCount, track, trackError, showErrorSnackbar, t],
	);

	const handleExport = useCallback((format: ExportFormat) => void runExport(format), [runExport]);

	return <GridExportButton onExport={handleExport} isExporting={isExporting} formats={formats} disabled={disabled} dataTestId={dataTestId} />;
});
